import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import SafeIcon from '../components/common/SafeIcon';
import { mockAgentProfile } from '../data/mockProfile';

export default function PublicAgentPage() {
  const { id } = useParams();
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // En producción, buscaríamos el agente por su ID o username en la base de datos
  const agent = mockAgentProfile; 

  // Si el agente no existe o tiene su landing desactivada
  if (!agent || (id !== agent.username && id !== agent.id) || !agent.isLandingActive) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    // Aquí iría la lógica para guardar el lead
    setIsSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] pb-24">
      {/* Hero Header Minimalista */}
      <div className="bg-[var(--bg-surface)] border-b border-[var(--border-subtle)] py-12 px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-8">
          <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-[var(--bg-base)] shadow-2xl flex-shrink-0">
            <img src={agent.image} alt={agent.name} className="w-full h-full object-cover" />
          </div>
          <div className="text-center md:text-left">
            <h1 className="text-4xl md:text-5xl font-black text-[var(--text-main)] tracking-tighter uppercase italic mb-2">
              {agent.name}
            </h1>
            <span className="inline-block px-4 py-1.5 gfi-gradient-bg text-brand-navy rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm">
              {agent.role}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* Columna Izquierda: Bio y Contacto */}
        <div>
          <h2 className="text-2xl font-black text-[var(--text-main)] uppercase italic tracking-tight mb-6">
            Mi Misión
          </h2>
          <p className="text-[var(--text-main)]/80 text-lg leading-relaxed font-medium mb-10">
            {agent.bio}
          </p>

          <div className="bg-[var(--bg-surface)] p-8 rounded-3xl border border-[var(--border-subtle)] shadow-sm">
            <h3 className="text-sm font-black text-[var(--text-main)] uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
              <SafeIcon name="Shield" className="w-5 h-5 text-brand-cyan" />
              ¿Por qué GFI Latino?
            </h3>
            <ul className="space-y-4">
              {[
                'Educación financiera 100% en español',
                'Estrategias personalizadas para tu familia',
                'Protección de patrimonio y legado'
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="mt-0.5 w-5 h-5 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                    <SafeIcon name="Check" className="w-3 h-3 text-brand-green" />
                  </div>
                  <span className="text-[var(--text-main)]/80 text-sm font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Columna Derecha: Formulario (Lead Capture) */}
        <div>
          <div className="bg-[var(--bg-surface)] p-8 md:p-10 rounded-3xl border border-[var(--border-subtle)] shadow-xl relative overflow-hidden">
            {/* Adorno visual */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-cyan/10 rounded-bl-full pointer-events-none" />
            
            <h2 className="text-2xl font-black text-[var(--text-main)] uppercase italic tracking-tight mb-2 relative z-10">
              Solicitar Información
            </h2>
            <p className="text-[var(--text-secondary)] text-sm mb-8 relative z-10">
              Déjame tus datos y me pondré en contacto contigo en las próximas 24 horas.
            </p>

            {isSubmitted ? (
              <div className="bg-brand-green/10 border border-brand-green/30 rounded-2xl p-8 text-center animate-fade-in">
                <div className="w-16 h-16 bg-brand-green rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <SafeIcon name="Check" className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-black text-[var(--text-main)] uppercase italic mb-2">¡Mensaje Enviado!</h3>
                <p className="text-[var(--text-main)]/80 text-sm font-medium">
                  Gracias por tu interés. Revisaré tu información y te contactaré muy pronto.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
                <div>
                  <label className="block text-[10px] font-black text-[var(--text-main)] uppercase tracking-widest mb-2">
                    Nombre Completo *
                  </label>
                  <input 
                    type="text" 
                    required
                    className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan transition-all"
                    placeholder="Ej. Juan Pérez"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[10px] font-black text-[var(--text-main)] uppercase tracking-widest mb-2">
                      Email *
                    </label>
                    <input 
                      type="email" 
                      required
                      className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-cyan transition-all"
                      placeholder="tu@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-[var(--text-main)] uppercase tracking-widest mb-2">
                      Teléfono
                    </label>
                    <input 
                      type="tel" 
                      className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-cyan transition-all"
                      placeholder="(555) 000-0000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-[var(--text-main)] uppercase tracking-widest mb-2">
                    Mensaje (Opcional)
                  </label>
                  <textarea 
                    rows="3"
                    className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-cyan transition-all resize-none"
                    placeholder="¿En qué área te gustaría enfocarte?"
                  />
                </div>

                <button 
                  type="submit"
                  className="w-full gfi-gradient-bg text-brand-navy py-4 rounded-xl font-black text-sm uppercase tracking-[0.2em] transition-all transform hover:scale-[1.02] shadow-lg mt-4"
                >
                  Enviar Información
                </button>

                {/* Texto Legal */}
                <p className="text-[9px] text-[var(--text-secondary)] leading-relaxed mt-6 text-center px-4">
                  Al hacer clic en "Enviar Información", aceptas ser contactado por este agente independiente afiliado a GFI Latino a través de los medios proporcionados (email, teléfono, SMS). Tus datos son confidenciales y no serán vendidos a terceros.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}